import { BELGIAN_ENTERPRISE_NUMBER } from 'constants/regex.ts'
import { removeNonNumbers } from './format.ts'
import { TFunction } from 'i18next'
import { DEFAULT_FILE_SIZE_LIMIT } from 'constants/constants'
import { Language } from '@boltenergy-be/shared'
import { formatFileSize } from '@boltenergy-be/design-system/utils'

/**
 * Validates a Belgian enterprise number
 *
 * @param {string} enterpriseNumber
 * @returns {boolean}
 */
export const validateBelgianEnterpriseNumber = (enterpriseNumber: string): boolean => {
  // Check if the enterprise number matches the expected format
  if (!BELGIAN_ENTERPRISE_NUMBER.test(enterpriseNumber)) {
    return false
  }
  const formattedEnterprisenumber = removeNonNumbers(enterpriseNumber)

  const firstEightNumbers = parseInt(formattedEnterprisenumber.substring(0, 8), 10)
  const rest = firstEightNumbers % 97
  const controlDigit = 97 - rest
  const lastTwoNumbers = parseInt(formattedEnterprisenumber.substring(8), 10)

  return controlDigit === lastTwoNumbers
}

/**
 * Returns the indexes of files in the given FileList that exceed the size limit
 *
 * @param {FileList | null} fileList
 * @param {number} limit - In bytes, defaults to 3 MB
 * @returns {number[]}
 */
export const getInvalidFileIndexes = (fileList: FileList | null, limit: number = DEFAULT_FILE_SIZE_LIMIT): number[] => {
  if (!fileList) return []

  return Array.from(fileList)
    .map((file, index) => (file.size > limit ? index : -1))
    .filter((index) => index !== -1)
}

/**
 * Validates that the number of files in the given FileList does not exceed the maximum allowed amount
 *
 * @param {FileList} fileList
 * @param {number} maxAmountFiles
 * @param {TFunction} t
 * @returns {boolean | string}
 */
export const validateFileAmount = (fileList: FileList, maxAmountFiles: number, t: TFunction<any>): boolean | string => {
  if (fileList && fileList.length > maxAmountFiles) {
    return t(($) => $.invalid.maxAmountOfFiles, { ns: 'validation', max: maxAmountFiles })
  }

  return true
}

/**
 * Validates the file size of every file in the given FileList
 *
 * @description This is in line with the bodyParser sizeLimit of the API function. Upload limit is technically 4mb, but we have to account for a 33% size increase on formatting to base64. Thus to ensure we stay under 4mb post formatting, the default limit is 3 MiB (3 * 1024 * 1024 bytes). (File: src/pages/api/customers/index.ts)
 * @param {FileList} fileList
 * @param {number} limit - In bytes, defaults to 3 MB (3 * 1024 * 1024)
 * @param {Language} locale
 * @param {TFunction} t
 * @returns {boolean | string}
 */
export const validateFileSize = (
  fileList: FileList,
  limit: number = DEFAULT_FILE_SIZE_LIMIT,
  locale: Language,
  t: TFunction<any>
): boolean | string => {
  const invalidIndexes = getInvalidFileIndexes(fileList, limit)

  if (invalidIndexes.length > 0) {
    return t(($) => $.invalid.fileSize, { ns: 'validation', size: formatFileSize(limit, locale) })
  }

  return true
}
